import React, { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { toast, ToastContainer } from 'react-toastify'
import Select from 'react-select'
import Topbar from '../../layouts/Topbar'
import Sidebar from '../../layouts/Sidebar'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api'

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com'

const roleOptions = [
  { value: 'employee', label: 'Employee' },
  { value: 'supervisor', label: 'Supervisor' },
]

const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: '0.375rem',
    borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#86b7fe' : '#c1c9d0'
    },
    minHeight: '38px'
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999
  })
}

const HODUpdateEmployee = () => {
  const baseUrl = API_BASE_URL
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const employeeId = location.state?.employeeId || id

  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingDepartments, setFetchingDepartments] = useState(true)
  const [fetchingEmployee, setFetchingEmployee] = useState(true)

  const [form, setForm] = useState({
    code: '',
    first_name: '',
    last_name: '',
    department: null,
    national_id: '',
    role: null,
    position: '',
    occupation: ''
  })

  useEffect(() => {
    const fetchDepartments = async () => {
      setFetchingDepartments(true)
      try {
        const token = Cookies.get('token')
        const response = await axios.get(`${baseUrl}/api/departments/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        setDepartments(response.data || [])
      } catch (err) {
        toast.error('Failed to fetch departments.')
      } finally {
        setFetchingDepartments(false)
      }
    }
    fetchDepartments()
  }, [baseUrl])

  useEffect(() => {
    const fetchEmployee = async () => {
      setFetchingEmployee(true)
      try {
        const token = Cookies.get('token')
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        const emp = response.data.employee || response.data
        if (emp) {
          const matchedRole = roleOptions.find(r => r.value === emp.user?.role) || (emp.user?.role ? { value: emp.user.role, label: emp.user.role } : null)
          setForm({
            code: emp.code || '',
            first_name: emp.first_name || '',
            last_name: emp.last_name || '',
            department: emp.department ? {
              value: emp.department,
              label: emp.department_name ? `${emp.department_name}${emp.course_center_name ? ` (${emp.course_center_name})` : ''}` : `Department ${emp.department}`
            } : null,
            national_id: emp.national_id || '',
            role: matchedRole,
            position: emp.position || '',
            occupation: emp.occupation || ''
          })
        }
      } catch (err) {
        toast.error('Failed to fetch employee data.')
      } finally {
        setFetchingEmployee(false)
      }
    }
    if (employeeId) fetchEmployee()
  }, [employeeId, baseUrl])

  const departmentOptions = departments.map(dep => ({
    value: dep.id,
    label: dep.course_center_name ? `${dep.name} (${dep.course_center_name})` : dep.name
  }))

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSelectChange = (selected, { name }) => {
    setForm({ ...form, [name]: selected })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const token = Cookies.get('token')
      await axios.put(`${baseUrl}/api/employees/${employeeId}/`, {
        code: form.code,
        first_name: form.first_name,
        last_name: form.last_name,
        department: form.department?.value,
        national_id: form.national_id,
        role: form.role?.value,
        position: form.position,
        occupation: form.occupation
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      toast.success('Employee updated successfully!')
      setTimeout(() => {
        navigate(-1)
      }, 1200)
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Failed to update employee.')
    } finally {
      setLoading(false)
    }
  }

  const isFormValid = Boolean(
    form.code &&
    form.first_name &&
    form.last_name &&
    form.department &&
    form.national_id &&
    form.role &&
    form.position &&
    form.occupation
  )

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <ToastContainer position="top-right" autoClose={3000} />
      <Topbar />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content pb-0">
          
          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
            <div className="my-auto mb-2 mb-md-0">
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate(-1)}
                >
                  <i className="ti ti-arrow-left me-1"></i> Back
                </button>
                <h4 className="page-title mb-0">Update Employee</h4>
              </div>
              <p className="text-muted mb-0 mt-1">Modify employee personal details, department, and system roles.</p>
            </div>
          </div>

          {/* Main Card */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-transparent border-bottom py-3">
              <h5 className="card-title mb-0 text-primary">
                <i className="ti ti-user-edit me-2"></i>Employee Details
              </h5>
            </div>
            <div className="card-body p-4">
              {fetchingEmployee ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <h6 className="text-muted fw-normal">Loading employee details...</h6>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {/* Code & Department */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Employee Code <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="code"
                        value={form.code}
                        onChange={handleChange}
                        required
                        className="form-control"
                        placeholder="e.g. EMP-001"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Department <span className="text-danger">*</span></label>
                      <Select
                        name="department"
                        value={form.department}
                        onChange={handleSelectChange}
                        options={departmentOptions}
                        placeholder={fetchingDepartments ? 'Loading departments...' : 'Select department'}
                        styles={selectCustomStyles}
                        isDisabled={loading || fetchingDepartments}
                      />
                    </div>

                    {/* First Name & Last Name */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">First Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="first_name"
                        value={form.first_name}
                        onChange={handleChange}
                        required
                        className="form-control"
                        placeholder="Enter first name"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Last Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="last_name"
                        value={form.last_name}
                        onChange={handleChange}
                        required
                        className="form-control"
                        placeholder="Enter last name"
                        disabled={loading}
                      />
                    </div>

                    {/* National ID & Role */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">National ID / Passport <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="national_id"
                        value={form.national_id}
                        onChange={handleChange}
                        required
                        className="form-control"
                        placeholder="Enter national ID"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Role <span className="text-danger">*</span></label>
                      <Select
                        name="role"
                        value={form.role}
                        onChange={handleSelectChange}
                        options={roleOptions}
                        styles={selectCustomStyles}
                        placeholder="Select role"
                        isDisabled={loading}
                      />
                    </div>

                    {/* Occupation & Position */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Occupation <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="occupation"
                        value={form.occupation}
                        onChange={handleChange}
                        required
                        className="form-control"
                        placeholder="Enter occupation"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Position <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="position"
                        value={form.position}
                        onChange={handleChange}
                        required
                        className="form-control"
                        placeholder="Enter job position"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex align-items-center justify-content-end gap-2 mt-4 pt-3 border-top">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => navigate(-1)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary px-4 d-flex align-items-center gap-2"
                      disabled={loading || !isFormValid}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span>Updating Employee...</span>
                        </>
                      ) : (
                        <>
                          <i className="ti ti-device-floppy fs-18"></i>
                          <span>Update Employee</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

export default HODUpdateEmployee